import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, MapPin } from "lucide-react";
import Layout from "../components/layout/Layout";
import useSeo from "../hooks/useSeo";

const PILLARS = [
  {
    n: "01",
    title: "Lokalt",
    body: "Fokus på Lidköping, Götene och Skara — nära både installation och service.",
  },
  {
    n: "02",
    title: "Tydligt",
    body: "Du ser lösning och prisnivå innan nästa steg bokas.",
  },
  {
    n: "03",
    title: "Personligt",
    body: "Direkt kontakt med Vido genom hela vägen från förfrågan till installation.",
  },
];

const FACTS = [
  {
    label: "Serviceområde",
    value: "Lidköping · Götene · Skara",
  },
  {
    label: "Upplägg",
    value: "Tydliga paket och priser",
  },
  {
    label: "Kontakt",
    value: "Direkt med Mikael",
  },
];

export default function OmVido() {
  useSeo({
    title: "Om Vido | Klimatteknik i Lidköping, Götene och Skara",
    description:
      "Vido Klimatteknik hjälper privatpersoner i Lidköping, Götene och Skara med värme, kyla och inomhusklimat.",
    path: "/om-vido",
  });

  return (
    <Layout>
      {/* Hero */}
      <section
        data-testid="om-hero"
        className="pt-12 md:pt-16 pb-14 md:pb-20"
      >
        <div className="container-vido grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 items-end">
          <div className="fade-in">
            <span className="eyebrow">Om Vido</span>
            <h1 className="display-h1 mt-5 max-w-[13ch]">
              Klimatteknik utan{" "}
              <span className="italic text-[color:hsl(var(--accent-terracotta))]">
                onödigt krångel
              </span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[color:hsl(var(--muted-ink))] max-w-[56ch] leading-relaxed">
              Vido Klimatteknik hjälper privatpersoner med värme, kyla och
              inomhusklimat — med tydliga alternativ, personlig kontakt och en
              rak väg från förfrågan till installation.
            </p>
          </div>

          <div className="fade-in">
            <div className="rounded-[24px] border border-[color:hsl(var(--line))] bg-white overflow-hidden">
              <div className="px-6 md:px-7 py-5 border-b border-[color:hsl(var(--line))]">
                <span className="eyebrow">Kort om Vido</span>
              </div>
              <dl>
                {FACTS.map((fact) => (
                  <div
                    key={fact.label}
                    className="grid grid-cols-[120px_1fr] gap-5 px-6 md:px-7 py-5 border-b border-[color:hsl(var(--line))] last:border-b-0"
                  >
                    <dt className="text-xs uppercase tracking-[0.14em] text-[color:hsl(var(--muted-ink))]">
                      {fact.label}
                    </dt>
                    <dd className="text-sm md:text-base font-medium text-[color:hsl(var(--ink))]">
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section
        data-testid="om-pillars"
        className="py-14 md:py-16 bg-white border-y border-[color:hsl(var(--line))]"
      >
        <div className="container-vido">
          <div className="max-w-2xl fade-in">
            <span className="eyebrow">Så jobbar Vido</span>
            <h2 className="display-h2 mt-4">Tre principer</h2>
          </div>

          <div className="mt-10 grid md:grid-cols-3 border-y border-[color:hsl(var(--line))] md:divide-x divide-[color:hsl(var(--line))]">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`om-pillar-${i}`}
                className="fade-in py-7 md:py-8 md:px-8 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 border-[color:hsl(var(--line))] last:border-b-0"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-2xl text-[color:hsl(var(--accent-terracotta))] font-medium leading-none">
                    {p.n}
                  </span>
                  <h3 className="font-serif text-2xl md:text-[28px] font-medium leading-tight">
                    {p.title}
                  </h3>
                </div>
                <p className="mt-4 text-sm md:text-base text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[34ch]">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal contact */}
      <section
        data-testid="om-contact"
        className="py-14 md:py-16"
      >
        <div className="container-vido">
          <div className="rounded-[28px] border border-[color:hsl(var(--line))] bg-gradient-to-br from-white to-[hsl(var(--surface))] p-7 md:p-10 lg:p-12 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-center fade-in">
            <div>
              <span className="eyebrow">Personlig kontakt</span>
              <h2 className="display-h2 mt-4 max-w-[12ch]">
                Du pratar direkt med Mikael.
              </h2>
              <p className="mt-5 text-base md:text-lg text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[50ch]">
                Har du ett projekt på gång? Berätta kort vad du behöver hjälp
                med så återkommer Vido med nästa steg.
              </p>
              <div className="mt-7">
                <Link
                  to="/kontakt"
                  data-testid="om-cta-link"
                  className="btn-primary"
                >
                  Skicka förfrågan
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="rounded-[22px] bg-white border border-[color:hsl(var(--line))] p-6 md:p-7">
              <div
                className="font-serif text-2xl md:text-3xl font-medium"
                data-testid="om-contact-name"
              >
                Mikael Larsson
              </div>

              <div className="mt-6 space-y-5">
                <a
                  href="tel:0793228187"
                  data-testid="om-contact-phone"
                  className="flex items-start gap-3 group"
                >
                  <Phone
                    size={18}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-xs text-[color:hsl(var(--muted-ink))] uppercase tracking-widest">
                      Telefon
                    </div>
                    <div className="mt-1 text-base font-medium text-[color:hsl(var(--ink))] group-hover:text-[color:hsl(var(--accent-terracotta))] transition-colors">
                      079-322 81 87
                    </div>
                  </div>
                </a>

                <a
                  href="mailto:mikael@vido.nu"
                  data-testid="om-contact-email"
                  className="flex items-start gap-3 group"
                >
                  <Mail
                    size={18}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-xs text-[color:hsl(var(--muted-ink))] uppercase tracking-widest">
                      E-post
                    </div>
                    <div className="mt-1 text-base font-medium text-[color:hsl(var(--ink))] group-hover:text-[color:hsl(var(--accent-terracotta))] transition-colors">
                      mikael@vido.nu
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-3">
                  <MapPin
                    size={18}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-xs text-[color:hsl(var(--muted-ink))] uppercase tracking-widest">
                      Serviceområde
                    </div>
                    <div className="mt-1 text-base font-medium text-[color:hsl(var(--ink))]">
                      Lidköping · Götene · Skara
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
